
import { IDateRange } from '@containers/calendar/calendar.helpter';
import { ISchedule, IScheduleEntry } from '@app-types/websocket/schedule.types';

interface IHandoverSetting {
  atSchool:boolean;
  hour:number;
  minute:number;
  location:string;
}


const createScheduleSide = (entries:IScheduleEntry[]):{[key: string]: IScheduleEntry} => {
  const side:{[key: string]: IScheduleEntry} = {};
  for (const entry of entries) {
    side[(""+entry.id)] = entry;
  }
  return side;
}

// find first and last entries
const findFirstAndLastEntry = (side: {[key: string]: IScheduleEntry;}) => {
  let firstEntry:IScheduleEntry|undefined;
  let lastEntry:IScheduleEntry|undefined;

  const entries = Object.values(side);

  for (const entry of entries) {
    if (firstEntry === undefined || firstEntry.startTime > entry.startTime) {
      firstEntry = entry;
    }
    if (lastEntry === undefined || lastEntry.endTime < entry.endTime) {
      lastEntry = entry;
    }
  }
  return {firstEntry:(firstEntry?JSON.parse(JSON.stringify(firstEntry)):undefined), 
          lastEntry:(lastEntry?JSON.parse(JSON.stringify(lastEntry)):undefined)}
}

// first and last entry can be the same (if there only is one entry)
const makeSideToMatch = (otherSide:IScheduleEntry[], firstEntry?:IScheduleEntry, lastEntry?:IScheduleEntry):IScheduleEntry[] => {

  if (otherSide.length === 0 ) { // taking care of some special corner cases
    if (firstEntry !== undefined && lastEntry !== undefined) {
      return [{
        id:1,
        startTime:firstEntry.startTime,
        startAtSchool:firstEntry.startAtSchool,
        startLoc:firstEntry.startLoc,
        endTime:lastEntry.endTime,
        endAtSchool:lastEntry.endAtSchool,
        endLoc:lastEntry.endLoc,
      }];
    } else {
      return [];
    }
  } 
  
  const result:IScheduleEntry[] = [];

  let id = firstEntry?firstEntry.id:1;

  if (firstEntry !== undefined && firstEntry.startTime < otherSide[0].startTime) {
    result.push({
      id,
      startTime:firstEntry.startTime,
      startAtSchool:firstEntry.startAtSchool,
      startLoc:firstEntry.startLoc,
      endTime:otherSide[0].startTime,
      endAtSchool:otherSide[0].startAtSchool,
      endLoc:otherSide[0].startLoc,
    });
    id+=1;
  }
  
  if (otherSide.length > 1) {
    let prevEntry = otherSide[0];
    for (let index = 1; index < otherSide.length; index++) {
      const entry = otherSide[index];
      result.push({
        id,
        startTime:prevEntry.endTime,
        startAtSchool:prevEntry.endAtSchool,
        startLoc:prevEntry.endLoc,
        endTime:entry.startTime,
        endAtSchool:entry.startAtSchool,
        endLoc:entry.startLoc,
      });
      id+=1;
      prevEntry = entry;
    }
  }
  
  if (lastEntry !== undefined && lastEntry.endTime > otherSide[otherSide.length-1].endTime) {
    result.push({
      id,
      startTime:otherSide[otherSide.length-1].endTime,
      startAtSchool:otherSide[otherSide.length-1].endAtSchool,
      startLoc:otherSide[otherSide.length-1].endLoc,
      endTime:lastEntry.endTime,
      endAtSchool:lastEntry.endAtSchool,
      endLoc:lastEntry.endLoc,
    });
    id+=1;
  }

  // console.log(JSON.stringify(result.map((e) => e.id)));
  // console.log(JSON.stringify(result[result.length-1]))
  return result;
}

const createArrayCopyAndSort = (scheduleEntries:{ [key:number]:IScheduleEntry; }) => {
  return (Object.values(scheduleEntries)).sort((a,b) => a.startTime-b.startTime).map((entry) => Object.assign({},entry));
}

const handoverLocationMatches = (left?:string, right?:string):boolean => {
  return (left || "") === (right || "");
}

const ensureScheduleConsistency = (schedule:ISchedule, futureFrom:number = Date.now()):ISchedule => {
  const sideA = createArrayCopyAndSort(schedule.data.scheduleEntriesParentA);
  const sideB = createArrayCopyAndSort(schedule.data.scheduleEntriesParentB);
  const allEntries = sideA.concat(sideB).sort((a,b) => {
    if (a.startTime === b.startTime) {
      return a.endTime - b.endTime;
    }
    return a.startTime - b.startTime;
  });

  let changed = false;

  for (let index = 0; index < allEntries.length - 1; index++) {
    const dropoff = allEntries[index];
    const pickup = allEntries[index + 1];

    if (dropoff.endTime < futureFrom) {
      continue;
    }

    if (pickup.startTime !== dropoff.endTime) {
      pickup.startTime = dropoff.endTime;
      changed = true;
    }
    if (pickup.startAtSchool !== dropoff.endAtSchool) {
      pickup.startAtSchool = dropoff.endAtSchool;
      changed = true;
    }
    if (!handoverLocationMatches(pickup.startLoc, dropoff.endLoc)) {
      pickup.startLoc = dropoff.endLoc;
      changed = true;
    }
  }

  if (!changed) {
    return schedule;
  }

  return {
    id:schedule.id,
    lastHandover:schedule.lastHandover,
    status:schedule.status,
    name:schedule.name,
    school:schedule.school,
    data:{
      children:schedule.data.children,
      parentA:schedule.data.parentA,
      parentB:schedule.data.parentB,
      scheduleEntriesParentA:createScheduleSide(sideA),
      scheduleEntriesParentB:createScheduleSide(sideB),
    }
  }
}

// All entries has to be in time order
const modifyTimePlace = (scheduleEntries:IScheduleEntry[], selectedDays:IDateRange[], pickup:IHandoverSetting[], dropoff:IHandoverSetting[], 
  additionalTime:number) => {
  for(const entry of scheduleEntries) {
    for (const dateRange of selectedDays) {
      if (entry.startTime >= dateRange.start && entry.startTime <= (dateRange.end + additionalTime)) {
        const day = (new Date(entry.startTime));
        const dayOfWeek = day.getDay();
        day.setHours(pickup[dayOfWeek].hour);
        day.setMinutes(pickup[dayOfWeek].minute);
        entry.startAtSchool = pickup[dayOfWeek].atSchool;
        entry.startLoc = pickup[dayOfWeek].location;
        entry.startTime = day.getTime();
      } 
      if (entry.endTime >= dateRange.start && entry.endTime <= (dateRange.end + additionalTime)) {
        const day = (new Date(entry.endTime));
        const dayOfWeek = day.getDay();
        day.setHours(dropoff[dayOfWeek].hour);
        day.setMinutes(dropoff[dayOfWeek].minute);
        entry.endAtSchool = dropoff[dayOfWeek].atSchool;
        entry.endLoc = dropoff[dayOfWeek].location;
        entry.endTime = day.getTime();
      }
    }
  }
  return scheduleEntries;
}

const getDefaultDays = (schedule:ISchedule|undefined):
  {parentATakesOver:IHandoverSetting[], parentBTakesOver:IHandoverSetting[]
  parentATakesOverDayVisitStart:IHandoverSetting[], parentBTakesOverDayVisitEnd:IHandoverSetting[], 
  parentBTakesOverDayVisitStart:IHandoverSetting[], parentATakesOverDayVisitEnd:IHandoverSetting[]} => {

    const parentATakesOver =  [{atSchool:true,hour:12,minute:0,location:""},
      {atSchool:true,hour:12,minute:0,location:""},
      {atSchool:true,hour:12,minute:0,location:""},
      {atSchool:true,hour:12,minute:0,location:""},
      {atSchool:true,hour:12,minute:0,location:""},
      {atSchool:true,hour:12,minute:0,location:""},
      {atSchool:true,hour:12,minute:0,location:""}];
    const parentBTakesOver = [{atSchool:true,hour:12,minute:0,location:""},
      {atSchool:true,hour:12,minute:0,location:""},
      {atSchool:true,hour:12,minute:0,location:""},
      {atSchool:true,hour:12,minute:0,location:""},
      {atSchool:true,hour:12,minute:0,location:""},
      {atSchool:true,hour:12,minute:0,location:""},
      {atSchool:true,hour:12,minute:0,location:""}];
    // indexed by Date.getDay(): weekend visits start at 11:00 since there is no school
    const parentATakesOverDayVisitStart =  [{atSchool:false,hour:11,minute:0,location:""},
      {atSchool:true,hour:12,minute:0,location:""},
      {atSchool:true,hour:12,minute:0,location:""},
      {atSchool:true,hour:12,minute:0,location:""},
      {atSchool:true,hour:12,minute:0,location:""},
      {atSchool:true,hour:12,minute:0,location:""},
      {atSchool:false,hour:11,minute:0,location:""}];
    const parentBTakesOverDayVisitEnd = [{atSchool:false,hour:13,minute:0,location:""},
      {atSchool:false,hour:13,minute:0,location:""},
      {atSchool:false,hour:13,minute:0,location:""},
      {atSchool:false,hour:13,minute:0,location:""},
      {atSchool:false,hour:13,minute:0,location:""},
      {atSchool:false,hour:13,minute:0,location:""},
      {atSchool:false,hour:13,minute:0,location:""}];
    const parentBTakesOverDayVisitStart =  [{atSchool:false,hour:11,minute:0,location:""},
      {atSchool:true,hour:12,minute:0,location:""},
      {atSchool:true,hour:12,minute:0,location:""},
      {atSchool:true,hour:12,minute:0,location:""},
      {atSchool:true,hour:12,minute:0,location:""},
      {atSchool:true,hour:12,minute:0,location:""},
      {atSchool:false,hour:11,minute:0,location:""}];
    const parentATakesOverDayVisitEnd = [{atSchool:false,hour:13,minute:0,location:""},
      {atSchool:false,hour:13,minute:0,location:""},
      {atSchool:false,hour:13,minute:0,location:""},
      {atSchool:false,hour:13,minute:0,location:""},
      {atSchool:false,hour:13,minute:0,location:""},
      {atSchool:false,hour:13,minute:0,location:""},
      {atSchool:false,hour:13,minute:0,location:""}];

    return {parentATakesOver, parentBTakesOver, 
      parentATakesOverDayVisitStart, parentBTakesOverDayVisitEnd, parentBTakesOverDayVisitStart, parentATakesOverDayVisitEnd};

}

// All entries has to be in time order
// merges overlapping entries
// removes entries inside time interval (if defined)
const mergeAndClean = (scheduleEntries:IScheduleEntry[]):IScheduleEntry[] => {
  const finalEntries:IScheduleEntry[] = [];
  let prevEntry:IScheduleEntry|undefined;

  for(const entry of scheduleEntries) {
      if (entry.startTime >= entry.endTime) { // invalid entry. skip it
          continue; 
      }
      if (prevEntry !== undefined) {
          if (prevEntry.endTime >= entry.startTime) { // these entries should be merged

              if (prevEntry.endTime < entry.endTime) {
                  prevEntry.endTime = entry.endTime;
                  prevEntry.endAtSchool = entry.endAtSchool;
                  prevEntry.endLoc = entry.endLoc;
              }
              continue; // skipå adding this entry we extended previous instead
          }
      }
      prevEntry = entry;
      finalEntries.push(entry);
  }
  return finalEntries;
};

// takes a broken schedule that is created from changerequests to make a mockup schedule that works.
const fixBrokenSchedule = (originalSchedule:ISchedule):ISchedule => {
  const origA = createArrayCopyAndSort(originalSchedule.data.scheduleEntriesParentA);
  const origB = createArrayCopyAndSort(originalSchedule.data.scheduleEntriesParentB);

  const fixedA:IScheduleEntry[] = [];
  const fixedB:IScheduleEntry[] = [];

  let indexA = 0;
  let indexB = 0;

  let prev: IScheduleEntry | undefined;
  let prevWasA:boolean = true;

  let indexFake:number = 6000000;
  while(indexA < origA.length || indexB < origB.length) {
    let nextA = (indexA < origA.length ? origA[indexA] : undefined);
    let nextB = (indexB < origB.length ? origB[indexB] : undefined);

    if (nextA !== undefined && nextB !== undefined) {
      if (nextA.startTime < nextB.startTime) {
        nextB = undefined;
      } else {
        nextA = undefined;
      }
    }

    if (nextA !== undefined) {
      if (prev !== undefined) {
        if (prevWasA) {
          indexFake++;
          fixedB.push({
            id:indexFake,
            startTime:prev.endTime,
            startLoc:prev.endLoc,
            startAtSchool:prev.endAtSchool,
            endTime:nextA.startTime,
            endLoc:nextA.startLoc,
            endAtSchool:nextA.startAtSchool,
          });
        } else {
          prev.endTime = nextA.startTime;
          prev.endLoc = nextA.startLoc;
          prev.endAtSchool = nextA.startAtSchool;
        }
      } else {
        // lets create a fake start
        indexFake++;
        fixedB.push({
          id:indexFake,
          startTime:nextA.startTime - (1000*60*60*24*3),
          startLoc:"",
          startAtSchool:true,
          endTime:nextA.startTime,
          endLoc:nextA.startLoc,
          endAtSchool:nextA.startAtSchool,
        });
      }
      fixedA.push(nextA);
      prev = nextA;
      prevWasA = true;

      indexA++;
    } 
    if (nextB !== undefined) {
      if (prev !== undefined) {
        if (!prevWasA) {
          indexFake++;
          fixedA.push({
            id:indexFake,
            startTime:prev.endTime,
            startLoc:prev.endLoc,
            startAtSchool:prev.endAtSchool,
            endTime:nextB.startTime,
            endLoc:nextB.startLoc,
            endAtSchool:nextB.startAtSchool,
          });
        } else {
          prev.endTime = nextB.startTime;
          prev.endLoc = nextB.startLoc;
          prev.endAtSchool = nextB.startAtSchool;
        }
      } else {
        indexFake++;
        fixedA.push({
          id:indexFake,
          startTime:nextB.startTime - (1000*60*60*24*3),
          startLoc:"",
          startAtSchool:true,
          endTime:nextB.startTime,
          endLoc:nextB.startLoc,
          endAtSchool:nextB.startAtSchool,
        });
      }
      fixedB.push(nextB);

      prev = nextB;
      prevWasA = false;
      indexB++;
    }
  }

  if (prev !== undefined) {
    const side = prevWasA?fixedB:fixedA;
    indexFake++;
    side.push({
      id:indexFake,
      startTime:prev.endTime,
      startLoc:prev.endLoc,
      startAtSchool:prev.endAtSchool,
      endTime:prev.endTime+(1000*60*60*24*3),
      endLoc:"",
      endAtSchool:true,
    })
  }

  fixedA.sort((a,b) => a.startTime-b.startTime);
  fixedB.sort((a,b) => a.startTime-b.startTime);
  return {
    data: {
      children: originalSchedule.data.children,
      parentA: originalSchedule.data.parentA,
      parentB: originalSchedule.data.parentB,
      scheduleEntriesParentA: createScheduleSide(fixedA),
      scheduleEntriesParentB: createScheduleSide(fixedB),
    },
    id:originalSchedule.id,
    lastHandover:originalSchedule.lastHandover,
    name:originalSchedule.name,
    school:originalSchedule.school,
    status:originalSchedule.status,

  }
}

export { mergeAndClean, modifyTimePlace, createArrayCopyAndSort, makeSideToMatch, findFirstAndLastEntry, 
  createScheduleSide, IHandoverSetting, getDefaultDays, fixBrokenSchedule, ensureScheduleConsistency };



// WEBPACK FOOTER //
// ./src/components/calendar2/editPane/editActions/ScheduleCalc.ts